// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Fetches knowledge base instances for a workspace from the KB subsystem API.
 * Uses react-query for caching and deduplication.
 */
import { useQuery } from '@tanstack/react-query'
import type { Services } from '@/app-shell/services'
import type { KnowledgeBaseOption } from '../types'

interface UseKnowledgeBaseInstancesParams {
  services: Services
  orgId?: string
  workspaceId?: string
}

export interface ResourceState<T> {
  data: T[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

interface KBInstanceResponse {
  instanceId: string
  label: string | null
  description: string | null
  mode: 'findings' | 'report' | 'criteria'
  itemCount?: number
  updatedAt: string | null
  updatedBy: string | null
}

export const useKnowledgeBaseInstances = ({
  services,
  orgId,
  workspaceId,
}: UseKnowledgeBaseInstancesParams): ResourceState<KnowledgeBaseOption> => {
  const enabled = Boolean(orgId && workspaceId)
  const client = services.getClient()

  const {
    data: knowledgeBases = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['kb-instances', orgId, workspaceId],
    queryFn: async () => {
      // Fetch KB instances from the knowledge base subsystem API
      const { data } = await client.get<KBInstanceResponse[]>(
        `/org/${orgId}/ws/${workspaceId}/knowledge_base/instances`
      )

      if (!data || !Array.isArray(data)) return []

      const kbOptions: KnowledgeBaseOption[] = data.map(instance => ({
        instanceId: instance.instanceId,
        label: instance.label?.trim() || instance.instanceId,
        description: instance.description?.trim() || null,
        mode: instance.mode,
        itemCount: instance.itemCount ?? 0,
        updatedAt: instance.updatedAt,
        updatedBy: instance.updatedBy,
      }))

      return kbOptions.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
    },
    enabled,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  return {
    data: knowledgeBases,
    loading: isLoading,
    error: error ? (error instanceof Error ? error.message : 'Unable to load knowledge base instances') : null,
    refresh: async () => { await refetch() },
  }
}
